<script lang="ts" setup>
import { Close } from '@element-plus/icons-vue'
import { ButtonComponent } from '@metapic-apps/shared'
import type { FormInstance, FormRules } from 'element-plus'
import { ElButton, ElMessageBox } from 'element-plus'
import { computed, reactive, ref, watch } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'

import CampaignFormAudience from '@/components/CampaignForm/components/CampaignFormAudience.vue'
import CampaignFormCalendar from '@/components/CampaignForm/components/CampaignFormCalendar.vue'
import CampaignFormContent from '@/components/CampaignForm/components/CampaignFormContent.vue'
import CampaignFormGeneral from '@/components/CampaignForm/components/CampaignFormGeneral.vue'
import CampaignFormNavigation from '@/components/CampaignForm/components/CampaignFormNavigation.vue'
import CampaignFormPayment from '@/components/CampaignForm/components/CampaignFormPayment.vue'
import type { CampaignFormData } from '@/lib'
import { defaultTrafficSourceGroup } from '@/lib/helpers/trafficSourceCosts.helpers'
import { useAdvertiserStore } from '@/stores/advertiser.stores'
import { type CampaignType } from '@/types'
import { type UserTag } from '@/types/userTags'
import BoxComponent from '@/ui-library/atoms/BoxComponent/BoxComponent.vue'
import FlexComponent from '@/ui-library/atoms/FlexComponent/FlexComponent.vue'
import TypographyComponent from '@/ui-library/atoms/TypographyComponent/TypographyComponent.vue'

const props = withDefaults(
  defineProps<{
    isLoading: boolean
    campaign?: CampaignType
    currencySymbol: string
    trafficSources: { source: number; name: string }[]
    advertiserUsesCpa: boolean
    userTags: UserTag[]
    recommendedPrice?: string
  }>(),
  {
    currencySymbol: '',
  },
)
const emit = defineEmits(['handleSubmit', 'handleBack'])

const advertiserStore = useAdvertiserStore()
const advertiserId = computed(() => advertiserStore.advertiser?.id)

const breakOffStoreIdInviteMetapicCreators = 29754
const isAllowedToInviteCreators = computed(() => advertiserId.value && advertiserId.value < breakOffStoreIdInviteMetapicCreators)

const isDirty = ref(false)
const container = ref()
const ruleFormRef = ref<FormInstance>()
const formData = reactive<CampaignFormData>(
  // @ts-expect-error todo fix me
  props.campaign
    ? { ...props.campaign, ...{ isAllowedToInviteCreators: isAllowedToInviteCreators.value } }
    : {
        title: '',
        description: '',
        logo: '',
        logoTemp: undefined,
        hasProductSeeding: false,
        hasTodoListEnable: false,
        todoList: [],
        hasStartNow: true,
        hasOpenEnd: true,
        hashtags: [],
        startDate: '',
        endDate: '',
        maxClicks: 0,
        hasIndividualLimitPerUserEnable: false,
        hasCommissionEnable: true,
        hasAdditionalPayment: false,
        additionalPayment: 0,
        groupedTrafficSourceCosts: [{ ...defaultTrafficSourceGroup }],
        isAllowedToInviteCreators: isAllowedToInviteCreators.value,
        hasInviteMetapicCreatorsEnable: isAllowedToInviteCreators.value,
        hasApproveCreatorsEnable: isAllowedToInviteCreators.value,
        tagIds: [],
        invitedUsers: [],
        segments: [],
      },
)

const rules = reactive<FormRules<CampaignFormData>>({
  title: [
    { required: true, message: 'This field is required', trigger: 'blur' },
    { max: 64, message: 'Field must be shorter than 64 symbols', trigger: 'blur' },
  ],
  description: [{ max: 1056, message: 'Field must be shorter than 1056 symbols', trigger: 'blur' }],
  additionalPayment: [
    { type: 'number', min: 0, message: 'Field must be a positive number', trigger: 'blur' },
    { type: 'number', max: 1000000, message: 'Field must be less than 1000000', trigger: 'blur' },
  ],
  maxClicks: [
    { type: 'number', min: 0, message: 'Field must be a positive number', trigger: 'blur' },
    { type: 'number', max: 1000000, message: 'Field must be less than 1000000', trigger: 'blur' },
  ],
  startDate: [{ type: 'date', message: 'Please pick a valid date', trigger: 'blur' }],
  endDate: [
    { type: 'date', message: 'Please pick a valid date', trigger: 'blur' },
    {
      validator: (_, value: Date, callback) => {
        if (formData.startDate && new Date(value).getTime() <= new Date(formData.startDate).getTime()) {
          callback(new Error('End date should be greater then start date'))
        } else {
          callback()
        }
      },
      trigger: 'blur',
    },
    {
      validator: (_, value: Date, callback) => {
        if (formData.hashtags?.length && !value) {
          callback(new Error('An end date is required when hashtags are set'))
        } else {
          callback()
        }
      },
      trigger: 'submit',
    },
  ],
})

watch(
  () => formData.hashtags?.length,
  async (length) => {
    if (length && formData.endDate) {
      await ruleFormRef.value?.validateField('endDate')
    }
  },
)

const submitForm = (formEl: FormInstance | undefined) => {
  if (!formEl) {
    return
  }

  return formEl.validate((valid) => {
    if (valid) {
      isDirty.value = false
      emit('handleSubmit', formData)
    }
  })
}

const handleClose = () => {
  emit('handleBack')
}

onBeforeRouteLeave((to, from, next) => {
  if (isDirty.value) {
    ElMessageBox.confirm('Any unsaved changes will be lost if you proceed. Do you want to discard your changes?', 'Discard changes', {
      confirmButtonText: 'Discard',
      cancelButtonText: 'Stay on page',
      roundButton: true,
    })
      .then(() => {
        next()
      })
      .catch(() => {
        next(false)
      })
  } else {
    next()
  }
})
</script>
<template>
  <ElButton text class="campaign-form__close-btn" @click="handleClose">
    <el-icon :size="24"><Close /></el-icon>
  </ElButton>
  <div ref="container" class="campaign-form__container">
    <TypographyComponent variant="headline-large" class="campaign-form__wrapper">{{
      campaign ? 'Edit campaign' : 'Create a campaign'
    }}</TypographyComponent>
    <BoxComponent :mt="24">
      <FlexComponent justify="center">
        <CampaignFormNavigation :skip-audience="Boolean(campaign)" />
        <el-form
          ref="ruleFormRef"
          :model="formData"
          :rules="rules"
          hide-required-asterisk
          :validate-on-rule-change="false"
          :scroll-to-error="true"
          label-position="top"
          class="campaign-form__content"
          @change="isDirty = true"
        >
          <!-- @vue-expect-error todo fix me-->
          <CampaignFormGeneral
            id="campaign-form-block-general"
            v-model:title="formData.title"
            v-model:description="formData.description"
            v-model:logo-temp="formData.logoTemp"
            v-model:has-product-seeding="formData.hasProductSeeding"
            v-model:has-todo-list="formData.hasTodoListEnable"
            v-model:todo-list="formData.todoList"
            :is-editing="Boolean(props.campaign)"
            :image="campaign?.image"
          />
          <CampaignFormContent id="campaign-form-block-content" v-model:hashtags="formData.hashtags" />
          <CampaignFormPayment
            id="campaign-form-block-payment"
            v-model:grouped-traffic-source-costs="formData.groupedTrafficSourceCosts"
            v-model:has-commission="formData.hasCommissionEnable"
            v-model:has-additional-payment="formData.hasAdditionalPayment"
            v-model:additional-payment="formData.additionalPayment"
            :recommended-price="recommendedPrice"
            :currency-symbol="currencySymbol"
            :traffic-sources="trafficSources"
            :advertiser-uses-cpa="advertiserUsesCpa"
          />
          <!-- @vue-expect-error todo fix me-->
          <CampaignFormCalendar
            id="campaign-form-block-calendar"
            v-model:start-date="formData.startDate"
            v-model:end-date="formData.endDate"
            v-model:has-individual-limit-per-user="formData.hasIndividualLimitPerUserEnable"
            v-model:has-start-now="formData.hasStartNow"
            v-model:has-open-end="formData.hasOpenEnd"
            v-model:max-clicks="formData.maxClicks"
            :currency-symbol="currencySymbol"
            :has-hashtags="formData.hashtags.length > 0"
          />
          <CampaignFormAudience
            v-if="!campaign"
            id="campaign-form-block-audience"
            v-model:has-invite-metapic-creators-enable="formData.hasInviteMetapicCreatorsEnable"
            v-model:has-approve-creators-enable="formData.hasApproveCreatorsEnable"
            v-model:tag-ids="formData.tagIds"
            v-model:invited-users="formData.invitedUsers"
            v-model:segments="formData.segments"
            :user-tags="userTags"
            :is-allowed-to-invite-creators="isAllowedToInviteCreators"
          />
          <FlexComponent justify="flex-end">
            <ButtonComponent
              variant="accent"
              :is-loading="isLoading"
              class="campaign-form__button"
              @click.prevent="submitForm(ruleFormRef)"
            >
              Save
            </ButtonComponent>
          </FlexComponent>
        </el-form>
      </FlexComponent>
    </BoxComponent>
  </div>
</template>
<style lang="scss" scoped>
$breakpoint-lg: 992px;

.text-color {
  color: var(--primary-color);
}

:deep(input[type='text']) {
  height: initial;
}

:deep(.el-radio.el-radio--large) {
  border-radius: 40px;
  background: #f3f7f9;
  border-color: #f3f7f9;
  color: #000000;
  padding: 10px 24px;
  margin-right: 8px;

  &.is-checked {
    background: #000000;
    border-color: #000000;
    color: #fff;
  }

  .el-radio__label {
    padding: 0;
  }

  .el-radio__input.is-checked + .el-radio__label {
    color: #fff;
  }

  .text-color {
    color: inherit;
  }
}
:deep(.el-radio__input) {
  display: none;
}
:deep(.el-input-group__append) {
  background-color: #fff;
  border-left: 1px solid #fff;
}

.campaign-form__container {
  position: relative;
  margin: 0 auto;
  max-width: 964px;
  padding: 0 16px 24px 16px;
}

.campaign-form__wrapper {
  max-width: 700px;
  position: sticky;
  top: 0;
  margin: 0 auto;
  padding: 24px 0;
  background: #fff;
  z-index: 3;

  @media (min-width: $breakpoint-lg) {
    max-width: 100%;
    padding: 48px 0;
  }
}

.campaign-form__content {
  width: 100%;
  max-width: 700px;
}

.campaign-form__block {
  border-radius: 16px;
  background: #fff;
  box-shadow: 4px 4px 32px 0 rgba(0, 0, 0, 0.08);
}

.campaign-form__close-btn {
  position: fixed;
  z-index: 100;
  padding: 8px;
  top: 16px;
  right: 16px;

  @media (min-width: $breakpoint-lg) {
    top: 48px;
    right: 48px;
  }
}
</style>
